import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Trash2, Upload, X } from "lucide-react";

const MAX_SPONSORS = 3;
const MAX_IMAGE_BYTES = 500_000; // 500 KB per tile (base64-encoded inline)

/**
 * Per-pub local sponsors editor. Lives inside `/c/[slug]/editor`.
 * Each pub configures its own message + up to 3 sponsor tiles that render
 * on the branded login/register pages instead of Dugsy's global panel.
 */
export function LocalSponsorsCard({ pub, scope, onSaved }) {
  const applyScope = scope || ((path) => path);
  const [message, setMessage] = useState(pub.sponsorship_message || "");
  const [sponsors, setSponsors] = useState(() =>
    (pub.sponsors || []).map((s) => ({ ...s, _uid: crypto.randomUUID() })),
  );
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => {
    setMessage(pub.sponsorship_message || "");
    setSponsors((pub.sponsors || []).map((s) => ({ ...s, _uid: crypto.randomUUID() })));
  }, [pub.id, pub.sponsorship_message, pub.sponsors]);

  const readImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const onFile = async (idx, file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Sponsor image must be under 500 KB.");
      return;
    }
    try {
      const dataUrl = await readImage(file);
      setSponsors((prev) => {
        const next = [...prev];
        next[idx] = { ...(next[idx] || {}), image_url: dataUrl };
        return next;
      });
    } catch (e) {
      toast.error("Could not read image file.");
    }
  };

  const updateField = (idx, field, value) => {
    setSponsors((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || {}), [field]: value };
      return next;
    });
  };

  const addSlot = () => {
    if (sponsors.length >= MAX_SPONSORS) return;
    setSponsors((prev) => [
      ...prev,
      { image_url: "", alt_text: "", target_url: "", _uid: crypto.randomUUID() },
    ]);
  };

  const removeSlot = (idx) => {
    setSponsors((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    const cleaned = sponsors
      .filter((s) => (s.image_url || "").trim())
      .map((s) => ({
        image_url: s.image_url.trim(),
        alt_text: (s.alt_text || "").trim() || null,
        target_url: (s.target_url || "").trim() || null,
      }));
    setSaving(true);
    try {
      await api.put(applyScope(`/clients/me/pubs/${pub.id}`), {
        sponsors: cleaned,
        sponsorship_message: message,
      });
      toast.success("Local sponsors saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-sm border border-zinc-200 p-5" data-testid={`local-sponsors-card-${pub.slug}`}>
      <div className="flex items-center gap-2 mb-4">
        <Megaphone className="h-4 w-4 text-zinc-500" />
        <h3 className="font-heading text-base font-bold text-zinc-950">Local sponsors — {pub.name}</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Add up to 3 sponsor tiles + a short message. These replace Dugsy&apos;s global panel on <span className="font-mono">/{pub.slug}</span> and its register page. Leave empty to show only your pub logo.
      </p>

      <div className="space-y-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Sponsorship message</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={280}
            rows={2}
            placeholder="e.g. WIN vouchers to spend on food & drink — Mon-Thu at The Founders Arms."
            data-testid={`sponsorship-message-${pub.slug}`}
            className="mt-2 rounded-sm border-zinc-300 text-sm"
          />
        </div>

        {sponsors.map((s, i) => (
          <div key={s._uid} className="border border-zinc-200 rounded-sm p-3 space-y-2 relative" data-testid={`sponsor-slot-${pub.slug}-${i}`}>
            <button
              type="button"
              onClick={() => removeSlot(i)}
              data-testid={`sponsor-remove-${pub.slug}-${i}`}
              className="absolute top-2 right-2 text-zinc-400 hover:text-red-600"
              aria-label="Remove sponsor"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-20 h-20 border border-zinc-200 rounded-sm bg-zinc-50 flex items-center justify-center overflow-hidden">
                {s.image_url ? (
                  <img src={s.image_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">No image</span>
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  ref={(el) => { fileRefs.current[i] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={(e) => onFile(i, e.target.files?.[0])}
                  className="hidden"
                  data-testid={`sponsor-file-${pub.slug}-${i}`}
                />
                <button
                  type="button"
                  onClick={() => fileRefs.current[i]?.click()}
                  data-testid={`sponsor-upload-${pub.slug}-${i}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8"
                >
                  <Upload className="h-3.5 w-3.5" /> {s.image_url ? "Replace image" : "Upload image"}
                </button>
                <Input
                  value={s.alt_text || ""}
                  onChange={(e) => updateField(i, "alt_text", e.target.value)}
                  placeholder="Tagline (e.g. Best Sunday roast in town)"
                  maxLength={280}
                  data-testid={`sponsor-alt-${pub.slug}-${i}`}
                  className="h-9 rounded-sm border-zinc-300 text-sm"
                />
                <Input
                  value={s.target_url || ""}
                  onChange={(e) => updateField(i, "target_url", e.target.value)}
                  placeholder="Link URL (https://...)"
                  maxLength={500}
                  data-testid={`sponsor-link-${pub.slug}-${i}`}
                  className="h-9 rounded-sm border-zinc-300 text-sm"
                />
              </div>
            </div>
          </div>
        ))}

        {sponsors.length < MAX_SPONSORS && (
          <button
            type="button"
            onClick={addSlot}
            data-testid={`sponsor-add-slot-${pub.slug}`}
            className="w-full border border-dashed border-zinc-300 hover:border-zinc-950 rounded-sm h-11 text-xs font-semibold text-zinc-600 hover:text-zinc-950"
          >
            + Add sponsor tile ({sponsors.length}/{MAX_SPONSORS})
          </button>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid={`sponsors-save-${pub.slug}`}
          className="h-9 rounded-sm bg-zinc-950 hover:bg-black text-white text-xs px-4"
        >
          {saving ? "Saving…" : "Save local sponsors"}
        </Button>
      </div>
    </div>
  );
}

export default LocalSponsorsCard;
